import { useState } from "react";
import PublicHeader from "@/components/layout/PublicHeader";
import { usePageTitle } from "@/lib/seo";
import { Link } from "react-router-dom";
import { MapPin, Clock, Briefcase, ChevronDown, Sparkles, Heart, Globe, Zap, X, Upload, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Live job postings. Edit this list to add/remove roles — no backend round-trip needed.
const JOBS = [
  {
    id: "senior-engineer-2026",
    title: "Senior Engineer",
    location: "Remote (Canada)",
    type: "Full-time",
    team: "Engineering",
    salary: "$120,000–$160,000 CAD + equity",
    summary: "Own the product end-to-end with our team. You'll ship features used by Canadian landlords every day.",
    responsibilities: [
      "Design and ship full-stack features used by landlords across Canada.",
      "Drive architecture decisions for billing, lease generation, and compliance.",
      "Build clean, modular services — you'll see your code in production within hours, not weeks.",
      "Own performance and reliability: chase sub-200ms API responses and sub-second page loads.",
      "Improve onboarding so new landlords sign their first lease in under five minutes.",
    ],
    requirements: [
      "4+ years building production web apps with React + Python (FastAPI or Django).",
      "Comfortable with SQL or document databases (MongoDB experience a plus).",
      "Hands-on with Stripe, webhooks, and asynchronous flows.",
      "Bias for shipping over planning — small, frequent releases.",
      "Eligible to work in Canada.",
    ],
    bonus: [
      "Experience with Canadian RTA / provincial tenancy law.",
      "Background in real estate, fintech, or PropTech.",
      "OSS contributions you're proud of.",
    ],
  },
  {
    id: "customer-success-canada-2026",
    title: "Customer Success Lead (Canada)",
    location: "Remote (Canada — any province)",
    type: "Full-time",
    team: "Customer",
    salary: "$75,000–$95,000 CAD + bonus",
    summary: "Be the human voice of Central Rentals — onboard new landlords, answer the phone (yes, the real phone), and turn happy customers into our best growth channel.",
    responsibilities: [
      "Own the first 30 days of every new landlord — phone, email, and in-app onboarding.",
      "Build guides for Starter, Professional, and Enterprise customers.",
      "Run live demos for prospects in any time zone.",
      "Surface product feedback weekly to engineering — closest to customers, loudest in product meetings.",
      "Drive strong net-revenue-retention and renewal outcomes.",
    ],
    requirements: [
      "3+ years in customer success, account management, or property management.",
      "You actually like talking to people on the phone.",
      "Strong written communication — every email reads like it was crafted, not copied.",
      "Tech-curious — you can navigate a web dashboard and screenshot a bug without help.",
      "Bilingual EN/FR a plus (Quebec coverage).",
    ],
    bonus: [
      "Property management or real estate operations background.",
      "Worked at an early-stage Canadian software company before.",
    ],
  },
  {
    id: "content-marketer-canadian-rentals-2026",
    title: "Content Marketing Manager (Canadian Rentals)",
    location: "Remote (Canada)",
    type: "Full-time",
    team: "Marketing",
    salary: "$80,000–$110,000 CAD",
    summary: "Own the Central Rentals blog, social, and SEO — turn 3 daily auto-generated drafts into the #1 Canadian landlord resource on Google.",
    responsibilities: [
      "Editorial review and polish on the 3 SEO posts our system publishes daily.",
      "Build a content calendar around provincial RTA changes, CRA deadlines, and seasonal landlord pain points.",
      "Own the Central Rentals LinkedIn and Facebook pages — community engagement, not just broadcasting.",
      "Run keyword research and ladder up our ranking for the 50 highest-intent Canadian landlord searches.",
      "Run email digests and trial-conversion campaigns via Resend.",
    ],
    requirements: [
      "4+ years in B2B software content marketing — at least 2 in fintech, PropTech, or real estate.",
      "Deeply Canadian — you know the difference between an N4 and an N5.",
      "Hands-on SEO chops: keyword research, on-page, technical, and link building.",
      "Strong editor's eye — you can fix a 1500-word LLM draft in 15 minutes flat.",
    ],
    bonus: [
      "Track record of growing a blog from <5K to >100K monthly organic visits.",
      "Bilingual EN/FR.",
    ],
  },
];

function ApplyModal({ job, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [cover, setCover] = useState("");
  const [resume, setResume] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim())  { toast.error("Please enter your name"); return; }
    if (!email.trim()) { toast.error("Please enter your email"); return; }
    if (!resume) { toast.error("Please attach your resume"); return; }
    if (resume.size > 10 * 1024 * 1024) { toast.error("Resume is larger than 10 MB"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("name", name);
    fd.append("email", email);
    fd.append("phone", phone);
    fd.append("role_id", job.id);
    fd.append("role_title", job.title);
    fd.append("cover_letter", cover);
    fd.append("resume", resume);
    try {
      // The shared axios instance defaults to Content-Type: application/json
      // which mangles multipart uploads (server sees the FormData as a JSON
      // body and reports "field required" for name/email/role_id/role_title).
      // Passing an explicit undefined removes the default so the browser
      // generates the correct multipart boundary itself.
      await api.post("/careers/apply", fd, {
        headers: { "Content-Type": undefined },
      });
      setDone(true);
      toast.success("Application sent — we'll be in touch.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send your application. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Could not send your application.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/60 backdrop-blur-sm p-0 sm:p-4" data-testid="apply-modal" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-600">Apply</div>
            <div className="font-display text-lg font-extrabold text-slate-900 mt-1 truncate">{job.title}</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="flex-none p-1.5 rounded-lg text-slate-500 hover:bg-slate-100" data-testid="apply-close">
            <X className="w-5 h-5"/>
          </button>
        </div>

        {done ? (
          <div className="p-8 text-center" data-testid="apply-done">
            <div className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center mx-auto">
              <Check className="w-7 h-7"/>
            </div>
            <h3 className="font-display text-xl font-extrabold text-slate-900 mt-4">Application sent!</h3>
            <p className="text-sm text-slate-600 mt-2">A confirmation is in your inbox. A real human will reply within 5 business days.</p>
            <button type="button" onClick={onClose} className="mt-6 px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 transition-all">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <div>
              <label className="text-xs font-bold text-slate-700">Full name</label>
              <input data-testid="apply-name" required value={name} onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-300 bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-sm"
                placeholder="Alex Martin"/>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5"><span>Email</span><span className="text-rose-600 font-bold ml-0.5">*</span></label>
                <input data-testid="apply-email" type="email" required aria-required="true" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-sm"
                  placeholder="alex@example.com"/>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700">Phone <span className="text-slate-400 font-normal">(optional)</span></label>
                <input data-testid="apply-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                  className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-sm"
                  placeholder="+1 780 555 0123"/>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700">Cover note <span className="text-slate-400 font-normal">(optional)</span></label>
              <textarea data-testid="apply-cover" rows={4} value={cover} onChange={(e) => setCover(e.target.value)}
                className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-sm resize-y"
                placeholder="Tell us why this role — keep it short and human."/>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700">Resume <span className="text-slate-500 font-normal">(PDF, DOC, DOCX — max 10 MB)</span></label>
              <label className="mt-1 flex items-center gap-3 px-3 py-3 rounded-lg border-2 border-dashed border-slate-300 hover:border-emerald-400 cursor-pointer transition-all" data-testid="apply-resume-dropzone">
                <Upload className="w-4 h-4 text-slate-400 flex-none"/>
                <span className="text-sm text-slate-600 truncate">{resume ? resume.name : "Click to choose your resume"}</span>
                <input data-testid="apply-resume" type="file" required
                  accept=".pdf,.doc,.docx,.txt,.rtf,.odt,application/pdf"
                  onChange={(e) => setResume(e.target.files?.[0] || null)}
                  className="hidden"/>
              </label>
            </div>
            <button data-testid="apply-submit" type="submit" disabled={busy}
              className="w-full px-6 py-3 rounded-full bg-emerald-600 text-white font-bold text-sm shadow hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed transition-all">
              {busy ? "Sending…" : "Submit application"}
            </button>
            <p className="text-[11px] text-slate-500 text-center">
              By submitting, you agree to be contacted about this role. We never share your resume.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

function JobCard({ job }) {
  const [open, setOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden hover:border-emerald-300 hover:shadow-md transition-all" data-testid={`job-${job.id}`}>
      <button
        type="button"
        className="w-full p-6 text-left flex items-start justify-between gap-4"
        onClick={() => setOpen(o => !o)}
        data-testid={`job-toggle-${job.id}`}
      >
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-600">{job.team}</div>
          <div className="font-display text-xl md:text-2xl font-extrabold text-slate-900 mt-1">{job.title}</div>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
            <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4"/>{job.location}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4"/>{job.type}</span>
            <span className="inline-flex items-center gap-1.5"><Briefcase className="w-4 h-4"/>{job.salary}</span>
          </div>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed">{job.summary}</p>
        </div>
        <ChevronDown className={`w-5 h-5 mt-2 text-slate-400 flex-none transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>
      {open && (
        <div className="border-t border-slate-200 bg-slate-50 p-6 space-y-5" data-testid={`job-body-${job.id}`}>
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">What you&apos;ll do</div>
            <ul className="mt-2 space-y-1.5 text-sm text-slate-700 list-disc list-inside">
              {job.responsibilities.map(r => <li key={r}>{r}</li>)}
            </ul>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">You probably have</div>
            <ul className="mt-2 space-y-1.5 text-sm text-slate-700 list-disc list-inside">
              {job.requirements.map(r => <li key={r}>{r}</li>)}
            </ul>
          </div>
          {job.bonus?.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">Bonus points</div>
              <ul className="mt-2 space-y-1.5 text-sm text-slate-700 list-disc list-inside">
                {job.bonus.map(r => <li key={r}>{r}</li>)}
              </ul>
            </div>
          )}
          <button
            type="button"
            onClick={() => setApplying(true)}
            data-testid={`apply-${job.id}`}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-600 text-white font-bold text-sm shadow hover:bg-emerald-700 transition-all"
          >
            Apply now
          </button>
          <div className="text-xs text-slate-500">
            Upload your resume in one click. We reply to every application within 5 business days.
          </div>
        </div>
      )}
      {applying && <ApplyModal job={job} onClose={() => setApplying(false)}/>}
    </div>
  );
}

export default function Careers() {
  usePageTitle("Careers — Central Rentals Canada");
  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />

      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-br from-emerald-50 via-white to-sky-50">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-16 md:py-24" data-testid="careers-hero">
          <div className="text-xs tracking-[0.22em] uppercase font-bold text-emerald-600">Careers at Central Rentals</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 mt-3">
            Help us build the future of Canadian rentals.
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-3xl leading-relaxed">
            We&apos;re a profitable team across Canada. We ship fast and talk to customers.
            If that sounds like your kind of place, read on.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            {[
              { icon: Heart, t: "Real humans" },
              { icon: Zap, t: "Ship daily" },
              { icon: Globe, t: "Remote-friendly" },
              { icon: Sparkles, t: "Equity for everyone" },
            ].map(({ icon: Icon, t }) => (
              <span key={t} className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-slate-200 text-sm font-semibold text-slate-700">
                <Icon className="w-3.5 h-3.5 text-emerald-600"/>{t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-14 md:py-20">
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">Perks &amp; benefits</div>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mt-2">
            The good stuff.
          </h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              ["Health, dental, vision", "Day-one coverage for you and dependents."],
              ["Equity for everyone", "Real stock options vesting over 4 years. You build it, you own it."],
              ["Remote-first", "Work from anywhere in Canada. We meet in person quarterly."],
              ["$2,000 / yr learning stipend", "Courses, books, conferences — your choice."],
              ["Home-office setup", "Top-tier laptop, monitor, chair, and desk — on us."],
              ["Four weeks PTO + holidays", "Plus the week between Christmas and New Year's, every year."],
            ].map(([t, d]) => (
              <div key={t} className="rounded-2xl border border-slate-200 p-5 hover:border-emerald-300 hover:shadow-md transition-all">
                <div className="font-display text-base font-bold text-slate-900">{t}</div>
                <div className="text-sm text-slate-600 mt-1.5 leading-relaxed">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open roles */}
      <section className="bg-slate-50 border-y border-slate-200 py-14 md:py-20">
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">Open roles</div>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mt-2">
            {JOBS.length} positions open right now.
          </h2>
          <div className="mt-8 space-y-4" data-testid="careers-jobs-list">
            {JOBS.map(j => <JobCard key={j.id} job={j}/>)}
          </div>
        </div>
      </section>

      {/* Spec apply */}
      <section className="py-14">
        <div className="max-w-3xl mx-auto px-5 md:px-8 text-center">
          <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900">
            Don&apos;t see your dream role?
          </h2>
          <p className="mt-3 text-slate-600">We hire ahead of need for exceptional people. Pick the closest role above and tell us what you&apos;d build if we hired you — we read every application.</p>
          <div className="mt-3 text-xs text-slate-500"><Link to="/diversity" className="underline text-emerald-700">We&apos;re an equal-opportunity employer.</Link></div>
        </div>
      </section>
    </div>
  );
}
